import React, { useEffect, useState } from 'react';
import styles from './cart.module.css';
import { Close, Minus, Plus } from '../../../Svg';
import { useDispatch, useSelector } from 'react-redux';
import { deleteProductfromcart, getAllCartProductThunk, setCartData, upadteCart } from '../../redux/cartSlice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatINR } from '../../utils/gstTotals';

const Cart = () => {
    const navigate = useNavigate()
    const { cartData, loading, refresh } = useSelector((state) => state.cart);
    const { isLoggedin } = useSelector((state) => state.customer)
    const dispatch = useDispatch();
    const [quantityErrors, setQuantityErrors] = useState({});
    const [editingProductId, setEditingProductId] = useState(null);
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [itemInputs, setItemInputs] = useState({});
    const [sortAscending, setSortAscending] = useState(null);

    useEffect(() => {
        dispatch(getAllCartProductThunk());
    }, [refresh]);

    useEffect(() => {
        if (cartData?.length > 0) {
            setSelectedItemIds(cartData.map((item) => item.productId));
            const initialInputs = {};
            cartData.forEach((item) => {
                initialInputs[item.productId] = {
                    customerNo: item.customerNo || '',
                    targetPrice: item.targetPrice || '',
                    leadTime: item.leadTime || '',
                };
            });
            setItemInputs(initialInputs);
        }
    }, [cartData]);

    // Validate quantity against minimumMultiplier
    const validateQuantity = (productId, value) => {
        const product = cartData?.find(p => p.productId === productId);
        const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
        const numValue = Number(value);

        if (value === '') {
            setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
            return false;
        }

        if (isNaN(numValue)) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
            return false;
        }

        if (numValue <= 0) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
            return false;
        }

        if (numValue % minMultiplier !== 0) {
            setQuantityErrors(prev => ({
                ...prev,
                [productId]: `Quantity must be in multiples of ${minMultiplier}`
            }));
            return false;
        }

        setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
        return true;
    };

    // Handle quantity change
    const handleQuantityChange = (productId, newQuantity) => {
        const data = {
            id: productId,
            quantity: newQuantity
        }

        // Only validate and update if we're not currently editing this product
        if (editingProductId !== productId) {
            const isValid = validateQuantity(productId, newQuantity);

            if (isValid) {
                dispatch(upadteCart(data));
                const updatedProducts = cartData?.map((product) => {
                    if (product.productId === productId) {
                        const parsedQuantity = parseInt(newQuantity, 10) || 0;
                        return { ...product, quantity: parsedQuantity };
                    }
                    return product;
                });
                dispatch(setCartData(updatedProducts));
            }
        } else {
            // Temporarily update the quantity while editing
            const updatedProducts = cartData?.map((product) => {
                if (product.productId === productId) {
                    const parsedQuantity = parseInt(newQuantity, 10) || 0;
                    return { ...product, quantity: parsedQuantity };
                }
                return product;
            });
            dispatch(setCartData(updatedProducts));
        }
    };

    const handleQuantityBlur = (productId) => {
        setEditingProductId(null);
        const product = cartData?.find(p => p.productId === productId);
        if (product) {
            const isValid = validateQuantity(productId, product.quantity);
            if (isValid) {
                dispatch(upadteCart({
                    id: productId,
                    quantity: product.quantity
                }));
            } else {
                // Reset to last valid quantity
                const updatedProducts = cartData?.map((p) => {
                    if (p.productId === productId) {
                        return { ...p, quantity: p.previousValidQuantity || 1 };
                    }
                    return p;
                });
                dispatch(setCartData(updatedProducts));
            }
        }
    };

    const handleQuantityFocus = (productId) => {
        // Store the current valid quantity before editing
        const updatedProducts = cartData?.map((product) => {
            if (product.productId === productId) {
                return { ...product, previousValidQuantity: product.quantity };
            }
            return product;
        });
        dispatch(setCartData(updatedProducts));
        setEditingProductId(productId);
    };

    const getBreakQtyDetails = (quantityTable, quantity) => {
        if (!quantityTable || !Array.isArray(quantityTable) || quantityTable.length === 0) {
            return { breakQty: 0, unitPrice: 0, finalPrice: 0 };
        }
        const sortedTable = [...quantityTable].sort((a, b) => (a?.breakQty || 0) - (b?.breakQty || 0));
        return sortedTable.find((entry) => quantity <= entry?.breakQty) || sortedTable[sortedTable.length - 1] || { breakQty: 0, unitPrice: 0, finalPrice: 0 };
    };

    const incrementQuantity = (productId) => {
        const product = cartData?.find((p) => p.productId === productId);
        if (product) {
            const newQuantity = product.quantity + (product.productDetails?.inventory?.minimumMultiplier || 1);
            handleQuantityChange(productId, newQuantity);
        }
    };

    const decrementQuantity = (productId) => {
        const product = cartData?.find((p) => p.productId === productId);
        if (product) {
            const newQuantity = Math.max(
                1,
                product.quantity - (product.productDetails?.inventory?.minimumMultiplier || 1)
            );
            handleQuantityChange(productId, newQuantity);
        }
    };

    const formatAmount = (val) => {
        const num = Number(val) || 0;
        if (num < 0) {
            return `-₹${Math.abs(num).toFixed(2)}`;
        }
        return `₹${num.toFixed(2)}`;
    };

    const calculateSubTotal = () => {
        return cartData?.reduce((total, product) => {
            const { unitPrice = 0 } = getBreakQtyDetails(
                product.productDetails?.quantityTable,
                product.quantity
            ) || {};
            return total + product.quantity * unitPrice;
        }, 0);
    };

    const calculateTax18 = () => {
        const subtotal = calculateSubTotal() || 0;
        return subtotal * 0.18;
    };

    const calculateTotal = () => {
        const subtotal = calculateSubTotal() || 0;
        return subtotal + calculateTax18();
    };

    const handleDelete = (id) => {
        dispatch(deleteProductfromcart(id))
    }

    const isQuantityValid = (productId) => {
        const product = cartData?.find(p => p.productId === productId);
        if (!product) return false;

        const quantity = product.quantity;
        const minMultiplier = product.productDetails?.inventory?.minimumMultiplier || 1;
        const availQty = product.productDetails?.inventory?.qtyAvailable ?? 0;
        const isOutOfStock = product.productDetails?.status === 'OUT OF STOCK' || availQty <= 0;

        if (isOutOfStock) return false;

        return quantity > 0 && quantity <= availQty && quantity % minMultiplier === 0;
    };

    const handleCheckout = () => {
        const hasOutOfStockItem = cartData?.some(item => {
            const availQty = item.productDetails?.inventory?.qtyAvailable ?? 0;
            const isOutOfStock = item.productDetails?.status === 'OUT OF STOCK' || availQty <= 0;
            return isOutOfStock || item.quantity > availQty;
        });

        if (hasOutOfStockItem) {
            return toast.error('Some items in your cart are out of stock or exceed available quantity. Please update your cart before checking out.');
        }

        const hasInvalidQuantity = cartData?.some(item => !isQuantityValid(item.productId));
        if (hasInvalidQuantity) {
            return toast.error('Please enter valid quantities (multiples of SPQ) for all items');
        }

        const hasZeroQuantity = cartData?.some((item) => item.quantity === 0);
        if (hasZeroQuantity) {
            return toast.error('Please update quantities greater than 0 before checking out');
        }

        navigate('/checkout');
    }

    if (cartData?.length <= 0 || !isLoggedin) {
        return (
            <div style={{ flex: 1, justifyContent: 'center', alignItems: 'center', display: 'flex', minHeight: '60vh' }}>
                <div className={styles.emptyCartCard}>
                    <div className={styles.emptyIcon}>🛒</div>
                    <div className={styles.emptyText}>No Product found</div>
                    <button className={styles.browseBtn} onClick={() => navigate('/categories')}>Browse Products</button>
                </div>
            </div>
        )
    }

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedItemIds(cartData?.map((item) => item.productId) || []);
        } else {
            setSelectedItemIds([]);
        }
    };

    const handleToggleSelectItem = (productId) => {
        setSelectedItemIds((prev) =>
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId]
        );
    };

    const handleInputChange = (productId, field, value) => {
        setItemInputs((prev) => ({
            ...prev,
            [productId]: {
                ...(prev[productId] || {}),
                [field]: value,
            },
        }));
    };

    const toggleAvailabilitySort = () => {
        if (sortAscending === null) setSortAscending(true);
        else if (sortAscending === true) setSortAscending(false);
        else setSortAscending(null);
    };

    const getSortedCartData = () => {
        if (!cartData) return [];
        let items = [...cartData];
        if (sortAscending !== null) {
            items.sort((a, b) => {
                const availA = a?.productDetails?.inventory?.qtyAvailable ?? 0;
                const availB = b?.productDetails?.inventory?.qtyAvailable ?? 0;
                return sortAscending ? availA - availB : availB - availA;
            });
        }
        return items;
    };

    const sortedCartData = getSortedCartData();

    return (
        <>
            <div className={styles.stepperContainer}>
                <div className={styles.stepperWrapper}>
                    <div className={`${styles.step} ${styles.activeStep}`}>
                        <span className={styles.stepText}>CART</span>
                    </div>
                    <div className={styles.stepDivider}></div>
                    <div className={styles.step}>
                        <span className={styles.stepText}>ADDRESS</span>
                    </div>
                    <div className={styles.stepDivider}></div>
                    <div className={styles.step}>
                        <span className={styles.stepText}>PAYMENT</span>
                    </div>
                </div>
                <div className={styles.secureBadge}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <span>100% SECURE</span>
                </div>
            </div>
            <div className={styles.cartContainer}>
                <div className={styles.shoppingCart}>
                    <div className={styles.itemsHeader}>
                        <span>{selectedItemIds?.length || 0}/{cartData?.length || 0} ITEMS SELECTED</span>
                    </div>

                    {/* B2B CATALOG TABLE VIEW */}
                    <div className={styles.b2bTableContainer}>
                        <div className={styles.b2bTableHeader}>
                            <div className={styles.colIndexHeader}>#</div>
                            <div className={styles.colDetailsHeader}>Product Details</div>
                            <div className={styles.colQtyHeader}>Quantity</div>
                            <div className={styles.colUnitPriceHeader}>Unit Price</div>
                            <div className={styles.colExtPriceHeader}>Extended Price</div>
                        </div>

                        <div className={styles.b2bTableBody}>
                            {sortedCartData?.map((product, idx) => {
                                const productDetails = product?.productDetails || {};
                                const overview = productDetails?.productOverview || {};
                                const sku = productDetails?.sku || productDetails?.catalogNumber || productDetails?._id?.substring(0, 9)?.toUpperCase() || `C${52191718 + idx}`;
                                const partNo = overview?.productNumber?.[0] || productDetails?.mpn || '6ES7972-0DA00-0AA0';
                                const brand = overview?.manufacturer?.[0]?.name || productDetails?.brand || 'SIEMENS';
                                const category = overview?.categories?.[0] || productDetails?.category || 'Box Accessories';
                                const minMultiplier = productDetails?.inventory?.minimumMultiplier || 1;
                                const availQty = productDetails?.inventory?.qtyAvailable ?? 0;
                                const isOutOfStock = productDetails?.status === 'OUT OF STOCK' || availQty <= 0;

                                const { unitPrice = 0 } = getBreakQtyDetails(
                                    productDetails?.quantityTable,
                                    product?.quantity
                                ) || {};

                                const formattedUnitPrice = (unitPrice).toFixed(4);
                                const extendedPrice = (unitPrice * product?.quantity).toFixed(2);
                                const productParam = overview?.productNumber?.[0] || productDetails?._id || sku;
                                const productUrl = `/productCode/${encodeURIComponent(productParam)}`;

                                return (
                                    <div key={product?.productId || idx} className={styles.b2bTableRow}>
                                        {/* Column 1: Index Number */}
                                        <div className={styles.colIndexNum}>{idx + 1}</div>

                                        {/* Column 2: Product Image & Details */}
                                        <div className={styles.colProductDetails}>
                                            <a
                                                href={productUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.productImgWrap}
                                            >
                                                {productDetails?.assets?.featuredImage?.[0] ? (
                                                    <img src={productDetails?.assets?.featuredImage[0]} alt={partNo} />
                                                ) : (
                                                    <div className={styles.placeholderImg}>📦</div>
                                                )}
                                            </a>
                                            <div className={styles.productInfoWrapper}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <a
                                                        href={productUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.partNoText}
                                                    >
                                                        {partNo}
                                                    </a>
                                                    <span className={styles.removeIconBtn} onClick={() => handleDelete(product?.productId)} title="Remove Item">
                                                        <Close />
                                                    </span>
                                                </div>
                                                <div className={styles.brandNameText}>{brand}</div>
                                            </div>
                                        </div>

                                        {/* Quantity */}
                                        <div className={styles.colQuantity}>
                                            <div className={styles.orderBtns}>
                                                <button
                                                    className={styles.quantityBtn}
                                                    onClick={() => decrementQuantity(product?.productId)}
                                                >
                                                    <Minus />
                                                </button>
                                                <input
                                                    type="text"
                                                    placeholder="1"
                                                    id={`quantity-${product.productId}`}
                                                    value={product.quantity}
                                                    onChange={(e) =>
                                                        handleQuantityChange(product.productId, e.target.value)
                                                    }
                                                    onBlur={() => handleQuantityBlur(product.productId)}
                                                    onFocus={() => handleQuantityFocus(product.productId)}
                                                    min="1"
                                                    step={minMultiplier}
                                                    className={styles.qtyBoxInput}
                                                />
                                                <button
                                                    className={styles.quantityBtn}
                                                    onClick={() => incrementQuantity(product?.productId)}
                                                >
                                                    <Plus />
                                                </button>
                                            </div>
                                            <div className={styles.stockStatusSubtext}>
                                                {availQty} In Stock
                                                {quantityErrors[product.productId] && (
                                                    <div className={styles.errorText}>
                                                        {quantityErrors[product.productId]}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Unit Price */}
                                        <div className={styles.colUnitPrice}>
                                            <span>₹{formattedUnitPrice}</span>
                                        </div>

                                        {/* Extended Price */}
                                        <div className={styles.colExtendedPrice}>
                                            <span>₹{extendedPrice}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className={styles.orderSum}>
                    <div className={styles.orderSummary}>
                        <h2>PRICE DETAILS ({cartData?.length || 0} Items)</h2>
                        <div className={styles.sumDeets}>
                            <p>Total Subtotal</p> <span>{formatINR(calculateSubTotal())}</span>
                        </div>
                        <div className={styles.sumDeets}>
                            <p>Tax (18%)</p> <span>{formatINR(calculateTax18())}</span>
                        </div>
                        <div className={styles.sumDeets}>
                            <p>Shipping & Handling</p> <span>FREE</span>
                        </div>
                        <div className={styles.sumDeets}>
                            <p>Total Amount</p> <span>{formatINR(calculateTotal())}</span>
                        </div>
                    </div>
                    <button
                        className={styles.loginButton}
                        onClick={handleCheckout}
                        disabled={cartData?.some(item => !isQuantityValid(item.productId))}
                    >
                        PLACE ORDER
                    </button>
                    <div className={styles.trustBadges}>
                        <span className={styles.trustItem}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M9 12l2 2 4-4"/>
                            </svg>
                            100% Authentic Parts
                        </span>
                        <span className={styles.trustItem}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#512DA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13" rx="2"/>
                                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                                <circle cx="5.5" cy="18.5" r="2.5"/>
                                <circle cx="18.5" cy="18.5" r="2.5"/>
                            </svg>
                            <span style={{ color: '#512DA8' }}>Fast Dispatch</span>
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Cart;


















